import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Api, { DEFAULT_ERROR_MESSAGE } from '../../../../api/Api';
import { GLOBAL_HD_DTU_ID } from '../../../../utils/common';
import {
  EVENT_ANALYTICS_TYPE_UPDATED,
  trackUpdate,
} from '../../../../utils/matomoEvents';
import { BASIC_ANALYTICS } from '../../../../types/analytics';

interface ScatterPlotLogicProps {
  coordinates: number[];
  timeStepInterval: string;
  pointLabel: string;
  isFwe?: boolean;
  fwePointName?: string;
  setScatterPlotPreview: (data: any) => void;
}

export const useScatterPlot = ({
  coordinates,
  timeStepInterval,
  pointLabel,
  isFwe,
  fwePointName,
  setScatterPlotPreview,
}: ScatterPlotLogicProps) => {
  const [updatedScatterPlotData, setUpdatedScatterPlotData] = useState([]);
  const [errorScatterPlot, setErrorScatterPlot] = useState('');
  const pointName = isFwe ? fwePointName ?? pointLabel : pointLabel;
  const abortControllerRef = useRef<AbortController | null>(null);

  const clearScatterPlotData = () => {
    setUpdatedScatterPlotData([]);
    setScatterPlotPreview((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        Data: {
          ...prev.Data,
          Items: null,
        },
      };
    });
  };

  const scatterPlotMutation = useMutation({
    mutationFn: async (values: any) => {
      const data = await Api.chart.getBasicChartV2(
        crypto.randomUUID(),
        pointName,
        values.datasetName,
        values.startDate,
        values.endDate,
        coordinates.join(),
        values.datasetName === GLOBAL_HD_DTU_ID
          ? parseInt(timeStepInterval)
          : 60,
        values.parameters,
        values.month
      );
      return { data, datasetName: values.datasetName };
    },
    onSuccess: ({ data, datasetName }) => {
      if (data?.Data?.Items) {
        setUpdatedScatterPlotData(data.Data.Items);
        trackUpdate(
          EVENT_ANALYTICS_TYPE_UPDATED,
          coordinates,
          datasetName,
          BASIC_ANALYTICS.SCATTER_PLOT.toLowerCase()
        );
        setErrorScatterPlot('');
      } else {
        clearScatterPlotData();
      }
    },
    onError: (error: any) => {
      setErrorScatterPlot(
        error?.response?.data?.Message || DEFAULT_ERROR_MESSAGE
      );
      clearScatterPlotData();
    },
  });

  const jointScatterPlotMutation = useMutation({
    mutationFn: async (values: any & { signal?: AbortSignal }) => {
      const {
        orderId,
        datasetName,
        parameters,
        orderId2,
        datasetName2,
        parameters2,
        startDate,
        endDate,
        month,
        signal,
      } = values;

      const data = await Api.chart.getJointScatterPlotV2(
        orderId || '',
        datasetName,
        parameters,
        orderId2 || '',
        datasetName2,
        parameters2,
        startDate,
        endDate,
        pointName,
        coordinates.join(),
        datasetName === GLOBAL_HD_DTU_ID || datasetName2 === GLOBAL_HD_DTU_ID
          ? parseInt(timeStepInterval)
          : 60,
        month,
        { signal }
      );

      return { data, datasetName, datasetName2 };
    },
    onSuccess: ({ data, datasetName, datasetName2 }) => {
      if (data?.Data?.Items) {
        setUpdatedScatterPlotData(data.Data.Items);
        setErrorScatterPlot('');
      } else {
        clearScatterPlotData();
        const isCmems =
          datasetName.includes('CMEMS') || datasetName2.includes('CMEMS');
        setErrorScatterPlot(
          isCmems ? 'Layer not available' : DEFAULT_ERROR_MESSAGE
        );
      }
    },
    onError: (error: any) => {
      if (
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
      ) {
        clearScatterPlotData();
        return;
      }
      setErrorScatterPlot(
        error?.response?.data?.Message || DEFAULT_ERROR_MESSAGE
      );
      clearScatterPlotData();
    },
  });

  const onUpdateScatterPlot = useCallback(
    async (values: any) => {
      scatterPlotMutation.mutate(values);
    },
    [scatterPlotMutation]
  );

  const onUpdateJointScatterPlot = useCallback(
    async (values: any) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      jointScatterPlotMutation.mutate({
        ...values,
        signal: controller.signal,
      });
    },
    [jointScatterPlotMutation]
  );

  const resetScatterPlot = () => {
    setErrorScatterPlot('');
    setUpdatedScatterPlotData([]);
  };

  const cancelScatterPlot = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    updatedScatterPlotData,
    errorScatterPlot,
    onUpdateScatterPlot,
    onUpdateJointScatterPlot,
    resetScatterPlot,
    cancelScatterPlot,
    loadingScatterPlot:
      scatterPlotMutation.isPending || jointScatterPlotMutation.isPending,
  };
};
